import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

/**
 * Detail panel for a selected search result: a headline, a set of counted sections, and the records in whichever
 * section is active.
 *
 * A section renders one of two ways, so the panel doesn't need to know what it's showing:
 *
 *   flat     { id, name, count, items: [{ ref, title, badge, badge_class, meta, amount }] }
 *   grouped  { id, name, count, warehouses: [{ name, meta, badge, badge_class, lines: [...] }] }
 *
 * A grouped section is used where the records only make sense under a heading - stock, where the same item sits in
 * several warehouses. Its lines carry a quantity and an optional link out:
 *   { quantity, ref, title, badge, badge_class, meta, link, link_label }
 *
 * The caller supplies the sections; the panel shows one record at a time and closing it returns to the list.
 */
@Component({
	selector: 'app-record-detail',
	templateUrl: './record-detail.component.html',
	styleUrls: ['./record-detail.component.scss']
})
export class RecordDetailComponent implements OnChanges {

	// What kind of thing is being shown, e.g. 'Customer'.
	@Input() kind = '';
	@Input() title = '';
	@Input() subtitle = '';

	// One-line roll-up under the headline, e.g. "across 2 projects · 2 sites · £11,030.50 quoted".
	@Input() summary = '';

	/**
	 * Overrides for the headline figure. Without them it reads the active section's count and name, which is what a
	 * customer or user panel wants; stock leads on the quantity held instead.
	 */
	@Input() bigValue = null;
	@Input() bigLabel = null;

	// [{ id, name, count, items: [...] }] or [{ id, name, count, warehouses: [...] }]
	@Input() sections: any[] = [];

	@Input() loading = false;

	// Link out to the full record.
	@Input() linkLabel = '';

	// Fallback currency for the amount column; each item normally carries its own, the way the quote, project,
	// invoice and PO lists show each record in the currency it was raised in.
	@Input() currencyId = null;

	// Organisation alias, for building the route to a record an item links out to.
	@Input() orgAlias = null;

	// Route for the "open the whole record" footer link, e.g. ['/', org, 'customer', 12].
	@Input() link = null;

	// Query parameters the footer link opens with, for a record that is opened over a list rather than at a route.
	@Input() linkQuery = null;

	// The footer link as the panel's main action rather than a quiet link, for a panel that exists to send you on.
	@Input() linkAsButton = false;

	/**
	 * Whether the panel lists counted sections at all. Without them it is a headline over whatever the caller projects
	 * into [record-body] - a journal entry is one piece of text and the record it belongs to, not a set of lists.
	 */
	@Input() sectioned = true;

	/**
	 * Whether the screen behind the panel can take a product. Only items carrying a product_id are addable - the
	 * stock item's Products section - so everything else is unaffected by this.
	 */
	@Input() canAdd = false;

	// Products already added, as { id, quantity }, so a line that has been taken says so rather than offering again.
	@Input() addedProducts: any[] = [];

	/**
	 * An action offered against each heading of a grouped section, e.g. reserving stock in the warehouse the group
	 * is for. The caller says what it's called and which sections offer it; without a label nothing is rendered, so
	 * the customer and user panels are unaffected.
	 */
	@Input() groupActionLabel = '';
	@Input() groupActionSections: string[] = [];

	/**
	 * Icon actions offered against each line of a grouped section, e.g. editing or dropping a reservation. Each is
	 * { id, icon, title, class }; the press is reported with the line it was made against.
	 */
	@Input() lineActions: any[] = [];
	@Input() lineActionSections: string[] = [];

	@Output() closed: EventEmitter<void> = new EventEmitter();
	@Output() add: EventEmitter<any> = new EventEmitter();
	@Output() groupAction: EventEmitter<any> = new EventEmitter();
	@Output() lineAction: EventEmitter<any> = new EventEmitter();

	activeSection = null;
	filter = '';

	ngOnChanges(changes: SimpleChanges): void {
		if (!changes.sections) return;

		// Keep the chosen section across a refresh where possible, otherwise fall back to the first with anything in it.
		const ids = (this.sections || []).map(section => section.id);
		// The caller blanks the panel while it re-reads, e.g. after a reservation is edited from it. That interim
		// says nothing about which section to show, so it must not reset the one being looked at.
		if (!ids.length) return;
		if (this.activeSection && ids.indexOf(this.activeSection) !== -1) return;

		const firstPopulated = (this.sections || []).find(section => section.count > 0);
		this.activeSection = firstPopulated ? firstPopulated.id : (ids.length ? ids[0] : null);
	}

	get section() {
		return (this.sections || []).find(s => s.id === this.activeSection) || null;
	}

	// A section that groups its records under headings rather than listing them flat.
	get isGrouped() {
		return !!this.section?.warehouses;
	}

	// Whether the section on screen is one the caller offers its per-group action on.
	get showsGroupAction() {
		return !!this.groupActionLabel && (this.groupActionSections || []).indexOf(this.activeSection) !== -1;
	}

	// The same question for the actions offered against an individual line.
	get showsLineActions() {
		return !!(this.lineActions || []).length && (this.lineActionSections || []).indexOf(this.activeSection) !== -1;
	}

	get headlineValue() {
		return this.bigValue !== null ? this.bigValue : (this.section?.count || 0);
	}

	get headlineLabel() {
		return this.bigLabel !== null ? this.bigLabel : this.section?.name;
	}

	get items() {
		return this.matching(this.section?.items || []);
	}

	/**
	 * Groups with nothing left after the filter drop out, so the panel doesn't show empty headings.
	 *
	 * Handed back untouched when nothing is being filtered. A getter that rebuilds its objects is read on every
	 * change detection pass, and *ngFor tracks by identity: new objects every pass meant the whole group - heading,
	 * lines and all - was destroyed and re-created constantly. A press on a link re-created the anchor between
	 * mousedown and mouseup, so the click landed on a detached element and nothing ever opened. groupKey keeps
	 * that from coming back when a filter is active and the clone is unavoidable.
	 */
	get groups() {
		const all = this.section?.warehouses || [];
		if (!this.filter.trim()) return all;

		return all
			.map(group => ({ ...group, lines: this.matching(group.lines || []) }))
			.filter(group => group.lines.length);
	}

	// Whether the filter box is worth showing at all.
	get filterable() {
		if (this.isGrouped) {
			return (this.section?.warehouses || []).reduce((total, g) => total + (g.lines?.length || 0), 0) > 5;
		}
		return (this.section?.items || []).length > 5;
	}

	// An item the caller can act on: a product line, where there is a screen behind the search to add it to.
	canAddItem(item) {
		return this.canAdd && !!item?.product_id;
	}

	isAdded(item) {
		return (this.addedProducts || []).some(added => added.id === item?.product_id);
	}

	// The backend supplies the route segments after the organisation; items without them stay plain text.
	itemLink(item) {
		if (!item.link?.length || !this.orgAlias) return null;
		return ['/', this.orgAlias, ...item.link];
	}

	// A second destination for a line whose reference is a record of its own, e.g. the request behind an allocation.
	refLink(item) {
		if (!item.ref_link?.length || !this.orgAlias) return null;
		return ['/', this.orgAlias, ...item.ref_link];
	}

	// Deliberately free of `this`: NgForOf calls a trackBy function with its own context, not the component's.
	groupKey(index, group) {
		return group.warehouse_id !== undefined && group.warehouse_id !== null ? group.warehouse_id : (group.name || index);
	}

	selectSection(section) {
		this.activeSection = section.id;
		this.filter = '';
	}

	private matching(rows) {
		const term = this.filter.trim().toLowerCase();
		if (!term) return rows;

		return rows.filter(row =>
			(row.title || '').toLowerCase().includes(term) ||
			(row.ref || '').toLowerCase().includes(term) ||
			(row.meta || '').toLowerCase().includes(term) ||
			(row.badge || '').toLowerCase().includes(term)
		);
	}

}
